import React from 'react';
import PropTypes from 'prop-types';
import { BADGES } from 'config/badges';

import { H3, Text } from 'components/core/Typography';
import { Tooltip } from 'components/core/Tooltip';
import { Image } from 'components/core/Image';
import { Link } from 'components/core/Link';
import { Group } from 'components/core/Page';
import { Button } from 'components/core/Button';
import { CardBadge } from 'components/core/CardBadge';
import { ResponsiveCarousel } from 'components/facility/ResponsiveCarousel/ResponsiveCarousel';
import FacilityReviewSummary from 'components/search/FacilityCard/FacilityReviewSummary';
import { ClickToShowNumberButton } from 'components/contact/ClickToShowNumberButton';

import { QuestionMark } from 'components/svg';
import ThumbsupIcon from 'assets/images/icons/thumbs-up.svg';
import pinIcon from 'assets/images/icons/informed-search/pin.svg';

import './vipFacilityCardHeader.scss';

export default function VipFacilityCardHeader({
	average,
	distance,
	facilityUrl,
	handleShowMap,
	hasReviews,
	name,
	reviewCount,
	tagLine,
	city,
	state,
	zip,
	noFollowFacilityLink,
	fetchSearchCityPhoneNumber,
	vipAccount,
	vipFacilityImages,
	staticMapUrl,
	facilityBadges,
	giftCardValue,
}) {
	const { logo, isAlternate, altLogo } = vipAccount;
	return (
		<div className="vip-facility-card-header">
			<Group>
				<Image
					src={ThumbsupIcon}
					alt="trusted partner"
					className="thumbs-up-icon"
				/>
				<Text
					size="tiny"
					weight="bold"
					className="trusted-partner"
					rightSpacing
				>
					{' '}
					Trusted Partner
				</Text>
				<Tooltip
					className="trusted-partner-tooltip"
					content={
						<Text size="tiny">
							Reserve here with confidence, this is one of our
							most trusted brands.
						</Text>
					}
					tooltipShowing
					onFocus
					clickOnMobile
				>
					<QuestionMark />
				</Tooltip>
				<Text
					size="small"
					className="trusted-partner-text"
				>
					Reserve here with confidence, this is one of our most
					trusted brands.
				</Text>
			</Group>
			<Group>
				<div className="vip-carousel-container">
					<ResponsiveCarousel
						className="vip-carousel"
						images={vipFacilityImages}
					/>
				</div>
				<Link
					onClick={handleShowMap}
					hideBorder
				>
					<Image
						src={staticMapUrl}
						alt="facility map"
						lazyLoad={true}
						flexImage
						className="vip-static-map"
					/>
				</Link>
			</Group>
			<div className="inner-content">
				<div className="logo-block-container">
					<div className="logo-container">
						<div className="logo-and-badges">
							<Link
								href={facilityUrl}
								rel={noFollowFacilityLink ? 'nofollow' : ''}
								hideBorder
								productClick
							>
								<Image
									src={!isAlternate ? logo : altLogo}
									className="vip-facility-logo"
									lazyLoad={true}
									alt={'falilityLogo'}
								/>
							</Link>
							<div className="vip-badges-desktop">
								{facilityBadges.map((badgeName) => {
									const {
										tooltipMessage,
										color,
										icon,
										name,
									} = BADGES[badgeName];
									return (
										<CardBadge
											key={tooltipMessage}
											color={color}
											parentPadding="vip-card"
											icon={icon}
											tooltipMessage={
												badgeName === 'giftcard'
													? tooltipMessage(
															giftCardValue
													  )
													: tooltipMessage
											}
										>
											<Text
												color="white"
												weight="bold"
												size="tiny"
												last
											>
												{name}
											</Text>
										</CardBadge>
									);
								})}
							</div>
						</div>
					</div>
				</div>
				<Group className="vip-badges">
					{facilityBadges.map((badgeName) => {
						const { tooltipMessage, color, icon, name } =
							BADGES[badgeName];
						return (
							<CardBadge
								key={tooltipMessage}
								color={color}
								parentPadding="vip-card"
								icon={icon}
								tooltipMessage={tooltipMessage}
								clickToCloseTooltip
							>
								<Text
									color="white"
									weight="bold"
									size="tiny"
									last
								>
									{name}
								</Text>
							</CardBadge>
						);
					})}
				</Group>
				<div className="storage-text">
					<Group>
						<Link
							href={facilityUrl}
							rel={noFollowFacilityLink ? 'nofollow' : ''}
							hideBorder
							productClick
						>
							<H3
								weight="bold"
								size="medium"
							>
								{name}
							</H3>
						</Link>
					</Group>
					<Group>
						{!tagLine ? (
							<div className="facility-address">
								<Text
									weight="light"
									rightSpacing
								>{`${city} ${state} ${zip}`}</Text>
								<Text weight="light">{distance}</Text>
							</div>
						) : (
							<Text>{tagLine}</Text>
						)}
					</Group>
					<Group>
						<ClickToShowNumberButton
							showAsLink
							outline
							size="standard"
							fetchPhoneNumber={fetchSearchCityPhoneNumber}
						/>
						<Button
							link
							onClick={handleShowMap}
							preContent={
								<Image
									src={pinIcon}
									className="pin-icon"
									width="12"
									height="16"
									alt="pin Icon"
								/>
							}
						>
							<Text
								color="black"
								weight="bold"
							>
								View on map
							</Text>
						</Button>
					</Group>
					<Group>
						{hasReviews && (
							<FacilityReviewSummary
								rating={average}
								reviewCount={reviewCount}
								facilityUrl={facilityUrl}
								noFollowFacilityLink={noFollowFacilityLink}
							/>
						)}
					</Group>
				</div>
			</div>
		</div>
	);
}

VipFacilityCardHeader.propTypes = {
	average: PropTypes.number,
	distance: PropTypes.string,
	facilityUrl: PropTypes.string,
	handleShowMap: PropTypes.func,
	name: PropTypes.string,
	reviewCount: PropTypes.number,
	tagLine: PropTypes.string,
	city: PropTypes.string,
	state: PropTypes.string,
	zip: PropTypes.string,
	hasReviews: PropTypes.bool,
	noFollowFacilityLink: PropTypes.bool,
	fetchSearchCityPhoneNumber: PropTypes.func.isRequired,
	vipAccount: PropTypes.object,
	vipFacilityImages: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string,
			atl: PropTypes.string,
		})
	),
	staticMapUrl: PropTypes.string,
	facilityBadges: PropTypes.arrayOf(PropTypes.string),
	giftCardValue: PropTypes.string,
};
